import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../config/supabase';
import { supabaseAdmin } from '../config/supabase';
import { storagePublicBaseUrl } from '../config/env';
import toast from 'react-hot-toast';
import BlockEditor from '../components/BlockEditor';
import { blockHasDisplayableContent } from '../utils/facebookVideoEmbed';
import { useAuth } from '../contexts/AuthContext';
import { logAction, SECTIONS, ACTIONS } from '../services/auditLog';
import {
  eventDatesToStorage,
  formatWarsawDateTime,
  isoToWarsawDatetimeLocal,
} from '../utils/warsawDateTime';
import './NewsManagement.css';
import { getHomePlProxyHeaders } from '../utils/proxyUploadHeaders';

async function uploadImageToHome(file, type = 'news') {
  const uploadUrl = process.env.REACT_APP_UPLOAD_URL?.trim();
  if (!uploadUrl) {
    return uploadImageToSupabase(file, 'events');
  }
  const headers = getHomePlProxyHeaders();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  const response = await fetch(uploadUrl, {
    method: 'POST',
    headers,
    body: formData,
  });
  const text = await response.text();
  if (!response.ok) {
    console.error('home.pl upload failed:', response.status, text);
    throw new Error(`Upload failed: ${response.status}`);
  }
  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    const lastBrace = text.lastIndexOf('{');
    if (lastBrace !== -1) {
      try {
        data = JSON.parse(text.slice(lastBrace));
      } catch (e2) {
        throw new Error('Serwer zwrócił nieprawidłową odpowiedź. Sprawdź REACT_APP_UPLOAD_URL i logi PHP.');
      }
    } else {
      throw new Error('Serwer zwrócił nieprawidłową odpowiedź.');
    }
  }
  if (!data.url) throw new Error('Upload response missing url');
  return data.url;
}

async function uploadImageToSupabase(file, type) {
  const bucket = type === 'events' ? 'event-images' : 'news-images';
  const ext = (file.name && file.name.split('.').pop()) || 'jpg';
  const fileName = `gallery/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${ext}`;
  const { error: uploadError } = await supabaseAdmin.storage
    .from(bucket)
    .upload(fileName, file, { contentType: file.type || 'image/jpeg', upsert: false });
  if (uploadError) {
    console.error('Supabase upload error:', uploadError);
    throw new Error(uploadError.message || 'Błąd przesyłania do Supabase');
  }
  const { data: { publicUrl } } = supabaseAdmin.storage.from(bucket).getPublicUrl(fileName);
  return publicUrl;
}

async function deleteImagesOnHome(urls) {
  if (!urls || urls.length === 0) return;
  const deleteUrl = process.env.REACT_APP_DELETE_URL;
  const headers = getHomePlProxyHeaders(true);
  const response = await fetch(deleteUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify({ urls }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`delete.php failed: ${response.status} ${text}`);
  }
}

const AUTOSAVE_INTERVAL_MS = 30 * 1000; // 30 sekund
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

function validateImageFile(file) {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return 'File type is not allowed. Only JPG, PNG and WebP are accepted.';
  }
  if (file.size > MAX_IMAGE_SIZE) {
    return 'File is too large. Maximum size is 5 MB.';
  }
  return null;
}

// Helper function to convert relative image path to full public URL (same as NewsManagement)
function getPublicImageUrl(path) {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  if (!storagePublicBaseUrl) return path;
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  return `${storagePublicBaseUrl}/storage/v1/object/public/event-images/${cleanPath}`;
}

export default function EventsManagement() {
  const { user, isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [listTab, setListTab] = useState('all');
  const [autosaveDraftId, setAutosaveDraftId] = useState(null);
  const [sendPushNotification, setSendPushNotification] = useState(true);
  const [selectPushRecipients, setSelectPushRecipients] = useState(false);
  const [selectedPushUserIds, setSelectedPushUserIds] = useState([]);
  const [pushRecipientsList, setPushRecipientsList] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    location: '',
    status: 'draft',
  });
  const [blocks, setBlocks] = useState([{ 
    type: 'text', 
    text: '', 
    align: 'left',
    bold: false,
    italic: false,
    underline: false
  }]);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [existingGalleryImages, setExistingGalleryImages] = useState([]);
  const [removedImageUrls, setRemovedImageUrls] = useState([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const formDataRef = useRef(formData);
  const blocksRef = useRef(blocks);
  const galleryFilesRef = useRef(galleryFiles);
  const existingGalleryImagesRef = useRef(existingGalleryImages);
  const removedImageUrlsRef = useRef(removedImageUrls);
  const sendPushNotificationRef = useRef(sendPushNotification);
  const selectPushRecipientsRef = useRef(selectPushRecipients);
  const selectedPushUserIdsRef = useRef(selectedPushUserIds);
  const lastChangeAtRef = useRef(0);
  const lastAutosaveAtRef = useRef(0);
  const galleryInputRef = useRef(null);
  formDataRef.current = formData;
  blocksRef.current = blocks;
  galleryFilesRef.current = galleryFiles;
  existingGalleryImagesRef.current = existingGalleryImages;
  removedImageUrlsRef.current = removedImageUrls;
  sendPushNotificationRef.current = sendPushNotification;
  selectPushRecipientsRef.current = selectPushRecipients;
  selectedPushUserIdsRef.current = selectedPushUserIds;

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    if (!showModal || !isAdmin || !selectPushRecipients) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabaseAdmin
        .from('users')
        .select('id, device_id, expo_push_token')
        .not('expo_push_token', 'is', null)
        .order('created_at', { ascending: false });
      if (!cancelled && !error) setPushRecipientsList(data || []);
    })();
    return () => { cancelled = true; };
  }, [showModal, isAdmin, selectPushRecipients]);

  const displayedEvents = listTab === 'drafts' ? events.filter((e) => e.status === 'draft') : events.filter((e) => e.status === 'published');

  // Convert blocks to HTML for backward compatibility (description field)
  function blocksToHtml(blocks) {
    if (!blocks || blocks.length === 0) return '';
    
    return blocks.map(block => {
      if (block.type === 'text') {
        let processed = block.text || '';
        
        // Apply formatting based on block properties
        if (block.bold) {
          processed = `<strong>${processed}</strong>`;
        }
        if (block.italic) {
          processed = `<em>${processed}</em>`;
        }
        if (block.underline) {
          processed = `<u>${processed}</u>`;
        }
        
        const alignStyle = `style="text-align:${block.align || 'left'}"`;
        return `<p ${alignStyle}>${processed}</p>`;
      } else if (block.type === 'image') {
        const url = block.url || '';
        const width = block.width || '100%';
        const align = block.align || 'left';
        const widthStyle = width === '100%' ? 'max-width: 100%; width: 100%;' : `max-width: ${width}; width: ${width};`;
        const alignStyle = `text-align: ${align};`;
        return `<div style="${alignStyle}"><img src="${url}" style="${widthStyle} height: auto; display: ${width === '100%' ? 'block' : 'inline-block'}; margin: 8px 0;" /></div>`;
      } else if (block.type === 'divider') {
        return '<hr/>';
      } else if (block.type === 'link') {
        const text = block.text || '';
        const url = block.url || '#';
        const alignStyle = `style="text-align:${block.align || 'left'}"`;
        // Only include link if URL is valid
        if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
          return `<p ${alignStyle}><a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a></p>`;
        }
        return `<p ${alignStyle}>${text}</p>`;
      } else if (block.type === 'facebookVideo' && block.url) {
        return `<p data-facebook-video="1">[Film z Facebooka]</p>`;
      }
      return '';
    }).join('');
  }

  // Convert HTML/description to blocks (for backward compatibility)
  function htmlToBlocks(html) {
    if (!html) return [{ 
      type: 'text', 
      text: '', 
      align: 'left',
      bold: false,
      italic: false,
      underline: false
    }];
    
    const blocks = [];
    
    // Split by <p> tags, <hr/>, or <hr> to preserve structure
    const parts = html.split(/(<p[^>]*>.*?<\/p>|<hr\s*\/?>)/gi);
    
    for (const part of parts) {
      if (!part || !part.trim()) continue;
      
      const trimmedPart = part.trim();
      
      // Check for divider
      if (/<hr\s*\/?>/i.test(trimmedPart)) {
        blocks.push({ type: 'divider' });
        continue;
      }
      
      // Check for paragraph
      const pMatch = trimmedPart.match(/<p[^>]*>(.*?)<\/p>/i);
      if (pMatch) {
        const pContent = pMatch[1];
        const pTag = pMatch[0];
        
        // Extract alignment
        let align = 'left';
        const alignMatch = pTag.match(/text-align:\s*(left|center|right)/i);
        if (alignMatch) {
          align = alignMatch[1].toLowerCase();
        }
        
        // Check if this is a link
        const linkMatch = pContent.match(/<a\s+href=["']([^"']+)["'][^>]*>([^<]+)<\/a>/i);
        if (linkMatch) {
          blocks.push({
            type: 'link',
            text: linkMatch[2].trim(),
            url: linkMatch[1] || '',
            align: align
          });
          continue;
        }
        
        // Extract text and formatting
        let text = pContent;
        let bold = /<strong>|<b>/i.test(text);
        let italic = /<em>|<i>/i.test(text);
        let underline = /<u>/i.test(text);
        
        // Remove formatting tags
        text = text.replace(/<\/?strong>|<\/?b>/gi, '');
        text = text.replace(/<\/?em>|<\/?i>/gi, '');
        text = text.replace(/<\/?u>/gi, '');
        text = text.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
        
        if (text) {
          blocks.push({
            type: 'text',
            text: text,
            align: align,
            bold: bold,
            italic: italic,
            underline: underline
          });
        }
      } else if (!/<[^>]+>/.test(trimmedPart)) {
        // Plain text without tags
        const text = trimmedPart.replace(/&nbsp;/g, ' ').trim();
        if (text) {
          blocks.push({
            type: 'text',
            text: text,
            align: 'left',
            bold: false,
            italic: false,
            underline: false
          });
        }
      }
    }
    
    return blocks.length > 0 ? blocks : [{ 
      type: 'text', 
      text: '', 
      align: 'left',
      bold: false,
      italic: false,
      underline: false
    }];
  }

  const uploadImage = async (file) => {
    const validationError = validateImageFile(file);
    if (validationError) throw new Error(validationError);
    try {
      return await uploadImageToHome(file, 'events');
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleGallerySelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const validFiles = [];
    const invalidReasons = [];

    files.forEach(file => {
      const err = validateImageFile(file);
      if (err) {
        invalidReasons.push(`${file.name}: ${err}`);
        return;
      }
      validFiles.push(file);
    });

    if (invalidReasons.length > 0) {
      toast.error(invalidReasons[0] + (invalidReasons.length > 1 ? ` (and ${invalidReasons.length - 1} more)` : ''));
    }

    if (validFiles.length > 0) {
      setGalleryFiles([...galleryFiles, ...validFiles]);

      // Preview wszystkich plików
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setGalleryPreviews(prev => [...prev, { file, preview: reader.result }]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeGalleryFile = (index) => {
    setGalleryFiles(galleryFiles.filter((_, i) => i !== index));
    setGalleryPreviews(galleryPreviews.filter((_, i) => i !== index));
  };

  // Mark existing image for removal on SAVE (same as NewsManagement)
  const markGalleryImageForRemoval = (imagePath) => {
    setRemovedImageUrls((prev) => [...prev, imagePath]);
    setExistingGalleryImages((prev) => prev.filter((img) => img.image_path !== imagePath));
  };

  // Reorder gallery images - move image up (same as NewsManagement)
  const moveGalleryImageUp = (index, isExisting) => {
    if (isExisting) {
      if (index === 0) return;
      const newImages = [...existingGalleryImages];
      [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
      setExistingGalleryImages(newImages);
    } else {
      if (index === 0) return;
      const newPreviews = [...galleryPreviews];
      [newPreviews[index - 1], newPreviews[index]] = [newPreviews[index], newPreviews[index - 1]];
      setGalleryPreviews(newPreviews);
      const newFiles = [...galleryFiles];
      [newFiles[index - 1], newFiles[index]] = [newFiles[index], newFiles[index - 1]];
      setGalleryFiles(newFiles);
    }
  };

  // Reorder gallery images - move image down (same as NewsManagement)
  const moveGalleryImageDown = (index, isExisting) => {
    if (isExisting) {
      if (index === existingGalleryImages.length - 1) return;
      const newImages = [...existingGalleryImages];
      [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
      setExistingGalleryImages(newImages);
    } else {
      if (index === galleryPreviews.length - 1) return;
      const newPreviews = [...galleryPreviews];
      [newPreviews[index], newPreviews[index + 1]] = [newPreviews[index + 1], newPreviews[index]];
      setGalleryPreviews(newPreviews);
      const newFiles = [...galleryFiles];
      [newFiles[index], newFiles[index + 1]] = [newFiles[index + 1], newFiles[index]];
      setGalleryFiles(newFiles);
    }
  };

  const loadEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('start_date', { ascending: false });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      toast.error('Błąd podczas ładowania wydarzeń');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetModalState = () => {
    if (galleryInputRef.current) galleryInputRef.current.value = '';
    setShowModal(false);
    setEditingEvent(null);
    setAutosaveDraftId(null);
    setFormData({
      title: '',
      description: '',
      start_date: '',
      end_date: '',
      location: '',
      status: 'draft',
    });
    setBlocks([{ type: 'text', text: '', align: 'left', bold: false, italic: false, underline: false }]);
    setSendPushNotification(true);
    setSelectPushRecipients(false);
    setSelectedPushUserIds([]);
    setGalleryFiles([]);
    setGalleryPreviews([]);
    setExistingGalleryImages([]);
    setRemovedImageUrls([]);
  };

  const hasFormContent = () => {
    if ((formData.title || '').trim()) return true;
    if ((formData.start_date || '').trim()) return true;
    return blocks.some(blockHasDisplayableContent);
  };

  const autosaveDraftLightRef = useRef(null);
  autosaveDraftLightRef.current = async () => {
    if (!showModal || uploadingGallery) return;
    const fd = formDataRef.current;
    const bl = blocksRef.current;
    const galleryFilesCurrent = galleryFilesRef.current;
    const existingCurrent = existingGalleryImagesRef.current;
    const removedCurrent = removedImageUrlsRef.current;
    const htmlFromBlocks = blocksToHtml(bl);
    const title = (fd.title || '').trim() || '(Bez tytułu)';
    const { start_date: startDate, end_date: endDate } = eventDatesToStorage(
      fd.start_date,
      fd.end_date
    );
    const location = fd.location || '';
    let eventId = null;
    try {
      if (editingEvent) {
        if (editingEvent.status === 'published') {
          const pushPayloadAutosave = {
            send_push_on_publish: sendPushNotificationRef.current,
            push_recipient_ids: (isAdmin && selectPushRecipientsRef.current && selectedPushUserIdsRef.current?.length > 0) ? selectedPushUserIdsRef.current : null,
          };
          const { data: existingDraft } = await supabaseAdmin
            .from('events')
            .select('id')
            .eq('parent_id', editingEvent.id)
            .eq('status', 'draft')
            .limit(1)
            .maybeSingle();
          if (existingDraft) {
            const { error } = await supabaseAdmin
              .from('events')
              .update({
                title,
                description: htmlFromBlocks || '',
                blocks: bl || [],
                start_date: startDate,
                end_date: endDate || null,
                location: location || null,
                updated_at: new Date().toISOString(),
                ...pushPayloadAutosave,
              })
              .eq('id', existingDraft.id);
            if (error) throw error;
            eventId = existingDraft.id;
          } else {
            const { data: inserted, error } = await supabaseAdmin
              .from('events')
              .insert({
                title,
                description: htmlFromBlocks || '',
                blocks: bl || [],
                start_date: startDate,
                end_date: endDate || null,
                location: location || null,
                status: 'draft',
                parent_id: editingEvent.id,
                ...pushPayloadAutosave,
              })
              .select('id')
              .single();
            if (error) throw error;
            if (inserted?.id) {
              eventId = inserted.id;
            }
          }
        } else {
          const { error } = await supabaseAdmin
            .from('events')
            .update({
              title,
              description: htmlFromBlocks || '',
              blocks: bl || [],
              start_date: startDate,
              end_date: endDate || null,
              location: location || null,
              updated_at: new Date().toISOString(),
              status: 'draft',
            })
            .eq('id', editingEvent.id);
          if (error) throw error;
          eventId = editingEvent.id;
        }
      } else {
        if (autosaveDraftId) {
          const { error } = await supabaseAdmin
            .from('events')
            .update({
              title,
              description: htmlFromBlocks || '',
              blocks: bl || [],
              start_date: startDate,
              end_date: endDate || null,
              location: location || null,
              updated_at: new Date().toISOString(),
              status: 'draft',
            })
            .eq('id', autosaveDraftId);
          if (error) throw error;
          eventId = autosaveDraftId;
        } else {
          if (!startDate) {
            toast.error('Autosave: ustaw datę rozpoczęcia, aby zapisać w roboczych');
            return;
          }
          const { data, error } = await supabaseAdmin
            .from('events')
            .insert({
              title,
              description: htmlFromBlocks || '',
              blocks: bl || [],
              start_date: startDate,
              end_date: endDate || null,
              location: location || null,
              status: 'draft',
            })
            .select('id')
            .single();
          if (error) throw error;
          if (data?.id) {
            eventId = data.id;
            setAutosaveDraftId(data.id);
          }
        }
      }
      // Usuń zdjęcia oznaczone do usunięcia (edycja)
      if (editingEvent && removedCurrent.length > 0) {
        const urlsToDelete = removedCurrent.filter((p) => p && (p.startsWith('http://') || p.startsWith('https://')));
        if (urlsToDelete.length > 0) await deleteImagesOnHome(urlsToDelete);
        const { error: removeError } = await supabaseAdmin
          .from('event_images')
          .delete()
          .eq('event_id', eventId)
          .in('image_path', removedCurrent);
        if (removeError) throw new Error(removeError.message || 'Błąd usuwania zdjęć z galerii');
      }
      // Upload galerii i zapis do event_images (tak jak przy normalnym zapisie)
      if (eventId && (galleryFilesCurrent.length > 0 || existingCurrent.length > 0)) {
        setUploadingGallery(true);
        try {
          const uploadedUrls = [];
          for (let i = 0; i < galleryFilesCurrent.length; i++) {
            const url = await uploadImageToHome(galleryFilesCurrent[i], 'events');
            uploadedUrls.push(url);
          }
          const { error: deleteError } = await supabaseAdmin
            .from('event_images')
            .delete()
            .eq('event_id', eventId);
          if (deleteError) throw new Error(deleteError.message || 'Błąd usuwania starych obrazów');
          const allImagePaths = [
            ...existingCurrent.map((img) => img.image_path),
            ...uploadedUrls,
          ];
          if (allImagePaths.length > 0) {
            const galleryUploads = allImagePaths.map((imagePath) => ({
              event_id: eventId,
              image_path: imagePath,
            }));
            const { error: galleryError } = await supabaseAdmin
              .from('event_images')
              .insert(galleryUploads)
              .select();
            if (galleryError) throw new Error(`Błąd zapisywania galerii: ${galleryError.message || 'Nieznany błąd'}`);
            const mainImagePath = allImagePaths[0];
            const { error: updateMainError } = await supabaseAdmin
              .from('events')
              .update({ main_image_path: mainImagePath })
              .eq('id', eventId);
            if (updateMainError) console.error('Error updating main_image_path:', updateMainError);
            setExistingGalleryImages([
              ...existingCurrent.filter((img) => !removedCurrent.includes(img.image_path)),
              ...uploadedUrls.map((path) => ({ id: null, image_path: path })),
            ]);
            setGalleryFiles([]);
            setGalleryPreviews([]);
            setRemovedImageUrls([]);
          }
        } catch (galleryErr) {
          console.error('Autosave gallery error:', galleryErr);
          toast.error(galleryErr.message || 'Błąd podczas zapisywania galerii w autosave');
          throw galleryErr;
        } finally {
          setUploadingGallery(false);
        }
      }
      lastAutosaveAtRef.current = Date.now();
      toast.success('Zapisano w roboczych', { duration: 2000 });
      loadEvents();
    } catch (err) {
      console.error('Autosave error:', err);
      toast.error('Autosave nie powiódł się: ' + (err.message || 'nieznany błąd'));
    }
  };

  useEffect(() => {
    if (!showModal) return;
    const t = setInterval(() => { autosaveDraftLightRef.current?.(); }, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [showModal, uploadingGallery, editingEvent?.id, autosaveDraftId]);

  useEffect(() => {
    if (showModal && !editingEvent && galleryInputRef.current) {
      galleryInputRef.current.value = '';
    }
  }, [showModal, editingEvent]);

  const saveDraftRevisionForPublished = async (publishedId) => {
    const fd = formDataRef.current;
    const bl = blocksRef.current;
    const existingCurrent = existingGalleryImagesRef.current;
    const galleryFilesCurrent = galleryFilesRef.current;
    const removedCurrent = removedImageUrlsRef.current;
    const htmlFromBlocks = blocksToHtml(bl);
    const title = (fd.title || '').trim() || '(Bez tytułu)';
    const draftStoredDates = eventDatesToStorage(fd.start_date, fd.end_date);
    const pushPayload = {
      send_push_on_publish: sendPushNotificationRef.current,
      push_recipient_ids: (isAdmin && selectPushRecipientsRef.current && selectedPushUserIdsRef.current?.length > 0) ? selectedPushUserIdsRef.current : null,
    };
    const { data: existingDraft } = await supabaseAdmin
      .from('events')
      .select('id')
      .eq('parent_id', publishedId)
      .eq('status', 'draft')
      .limit(1)
      .maybeSingle();
    let draftId;
    if (existingDraft) {
      const { error } = await supabaseAdmin
        .from('events')
        .update({
          title,
          description: htmlFromBlocks || '',
          blocks: bl || [],
          start_date: draftStoredDates.start_date,
          end_date: draftStoredDates.end_date,
          location: fd.location || null,
          updated_at: new Date().toISOString(),
          ...pushPayload,
        })
        .eq('id', existingDraft.id);
      if (error) throw error;
      draftId = existingDraft.id;
    } else {
      const { data: inserted, error } = await supabaseAdmin
        .from('events')
        .insert({
          title,
          description: htmlFromBlocks || '',
          blocks: bl || [],
          start_date: draftStoredDates.start_date,
          end_date: draftStoredDates.end_date,
          location: fd.location || null,
          status: 'draft',
          parent_id: publishedId,
          ...pushPayload,
        })
        .select('id')
        .single();
      if (error) throw error;
      draftId = inserted?.id;
    }
    if (!draftId) return;
    if (galleryFilesCurrent.length > 0 || existingCurrent.length > 0) {
      const uploadedUrls = [];
      for (let i = 0; i < galleryFilesCurrent.length; i++) {
        uploadedUrls.push(await uploadImageToHome(galleryFilesCurrent[i], 'events'));
      }
      await supabaseAdmin.from('event_images').delete().eq('event_id', draftId);
      const allImagePaths = [
        ...existingCurrent.filter((img) => !removedCurrent.includes(img.image_path)).map((img) => img.image_path),
        ...uploadedUrls,
      ];
      if (allImagePaths.length > 0) {
        await supabaseAdmin.from('event_images').insert(allImagePaths.map((imagePath) => ({ event_id: draftId, image_path: imagePath })));
        await supabaseAdmin.from('events').update({ main_image_path: allImagePaths[0] }).eq('id', draftId);
      }
    }
    resetModalState();
    loadEvents();
  };

  const handleCloseModal = async () => {
    if (uploadingGallery) return;
    if (!hasFormContent()) {
      if (autosaveDraftId) {
        await supabaseAdmin.from('events').delete().eq('id', autosaveDraftId);
      }
      resetModalState();
      loadEvents();
      return;
    }
    if (lastAutosaveAtRef.current > 0 && lastChangeAtRef.current <= lastAutosaveAtRef.current) {
      resetModalState();
      loadEvents();
      return;
    }
    if (window.confirm('Czy zapisać w roboczych?')) {
      try {
        if (editingEvent?.status === 'published') {
          await saveDraftRevisionForPublished(editingEvent.id);
        } else {
          await handleSubmitInternal({ preventDefault: () => {} }, 'draft');
        }
      } catch (err) {
        toast.error(err.message || 'Błąd zapisu');
      }
    } else {
      if (autosaveDraftId && !editingEvent) {
        await supabaseAdmin.from('events').delete().eq('id', autosaveDraftId);
      }
      resetModalState();
      loadEvents();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleSubmitInternal(e);
  };

  const handleSubmitInternal = async (e, forceStatus) => {
    e.preventDefault();
    const saveStatus = forceStatus ?? formData.status;
    const isDraftSave = forceStatus === 'draft';
    if (saveStatus === 'published' && sendPushNotification && isAdmin && selectPushRecipients && selectedPushUserIds.length === 0) {
      toast.error('Zaznacz co najmniej jednego odbiorcę push lub wyłącz „Wybierz odbiorców”');
      return;
    }
    const titleToSave = (formData.title || '').trim() || (isDraftSave ? '(Bez tytułu)' : '');
    try {
      if (isDraftSave && editingEvent?.status === 'published') {
        await saveDraftRevisionForPublished(editingEvent.id);
        return;
      }
      // Convert blocks to HTML for backward compatibility
      const htmlFromBlocks = blocksToHtml(blocks);

      // On EDIT: delete removed images from home.pl and event_images before updating (same as NewsManagement)
      if (editingEvent && removedImageUrls.length > 0 && !editingEvent.parent_id) {
        const urlsToDelete = removedImageUrls.filter((p) => p && (p.startsWith('http://') || p.startsWith('https://')));
        if (urlsToDelete.length > 0) await deleteImagesOnHome(urlsToDelete);
        const { error: removeError } = await supabaseAdmin
          .from('event_images')
          .delete()
          .eq('event_id', editingEvent.id)
          .in('image_path', removedImageUrls);
        if (removeError) throw new Error(removeError.message || 'Błąd usuwania zdjęć z galerii');
        // If user removed all images, clear main_image_path
        if (existingGalleryImages.length === 0 && galleryFiles.length === 0) {
          await supabaseAdmin.from('events').update({ main_image_path: null }).eq('id', editingEvent.id);
        }
      }

      const storedDates = eventDatesToStorage(formData.start_date, formData.end_date);

      let eventId;
      if (saveStatus === 'published' && editingEvent?.parent_id) {
        const parentId = editingEvent.parent_id;
        const { error: updateParentError } = await supabaseAdmin
          .from('events')
          .update({
            title: titleToSave || formData.title,
            description: htmlFromBlocks,
            blocks: blocks,
            start_date: storedDates.start_date,
            end_date: storedDates.end_date,
            location: formData.location,
            status: 'published',
            updated_at: new Date().toISOString(),
            send_push_on_publish: sendPushNotification,
            push_recipient_ids: (isAdmin && selectPushRecipients && selectedPushUserIds.length > 0) ? selectedPushUserIds : null,
          })
          .eq('id', parentId);
        if (updateParentError) throw updateParentError;
        const { data: draftImages } = await supabaseAdmin.from('event_images').select('image_path').eq('event_id', editingEvent.id);
        await supabaseAdmin.from('event_images').delete().eq('event_id', parentId);
        if (draftImages?.length) {
          await supabaseAdmin.from('event_images').insert(
            draftImages.map((row) => ({ event_id: parentId, image_path: row.image_path }))
          );
          await supabaseAdmin.from('events').update({ main_image_path: draftImages[0].image_path }).eq('id', parentId);
        }
        await supabaseAdmin.from('event_images').delete().eq('event_id', editingEvent.id);
        await supabaseAdmin.from('events').delete().eq('id', editingEvent.id);
        toast.success('Wydarzenie zaktualizowane (wersja robocza opublikowana)');
        await logAction(
          { section: SECTIONS.EVENTS, action: ACTIONS.UPDATE, entityType: 'event', entityId: parentId, entityLabel: formData.title?.trim(), details: 'Opublikowano wersję roboczą nadpisując opublikowane wydarzenie.' },
          user
        );
        if (sendPushNotification && parentId) {
          try {
            const pushTitle = `Nowe wydarzenie: ${titleToSave}`;
            const pushBody = titleToSave.length > 100 ? titleToSave.slice(0, 97) + '...' : titleToSave;
            const body = {
              title: pushTitle,
              body: pushBody,
              type: 'info',
              content_type: 'event',
              data: { type: 'event', screen: 'Events', eventId: String(parentId) },
            };
            if (isAdmin && selectPushRecipients && selectedPushUserIds.length > 0) {
              body.user_ids = selectedPushUserIds;
            }
            const { error: pushError } = await supabaseAdmin.functions.invoke('send-push-notification', { body });
            if (pushError) {
              console.error('Push notification error:', pushError);
              toast.error('Wydarzenie zapisane, ale wysyłka push nie powiodła się');
            } else {
              toast.success('Powiadomienie push wysłane');
            }
          } catch (pushErr) {
            console.error('Push notification error:', pushErr);
            toast.error('Wydarzenie zapisane, ale wysyłka push nie powiodła się');
          }
        }
        resetModalState();
        loadEvents();
        return;
      }

      if (editingEvent) {
        const wasAlreadyPublished = editingEvent.status === 'published';
        const publishedAt = saveStatus === 'published'
          ? (wasAlreadyPublished ? (editingEvent.published_at || new Date().toISOString()) : new Date().toISOString())
          : null;
        const updatePayload = {
          title: titleToSave || formData.title,
          description: htmlFromBlocks,
          blocks: blocks,
          start_date: storedDates.start_date,
          end_date: storedDates.end_date,
          location: formData.location,
          status: saveStatus,
          published_at: publishedAt,
          updated_at: new Date().toISOString(),
          send_push_on_publish: sendPushNotification,
          push_recipient_ids: (isAdmin && selectPushRecipients && selectedPushUserIds.length > 0) ? selectedPushUserIds : null,
        };

        const { error } = await supabaseAdmin
          .from('events')
          .update(updatePayload)
          .eq('id', editingEvent.id);

        if (error) {
          console.error('Supabase update error:', { code: error.code, message: error.message, payload: updatePayload });
          toast.error(`Błąd podczas aktualizacji: ${error.message || 'Nieznany błąd'}`);
          throw error;
        }
        eventId = editingEvent.id;
        toast.success('Wydarzenie zaktualizowane');
        await logAction(
          {
            section: SECTIONS.EVENTS,
            action: ACTIONS.UPDATE,
            entityType: 'event',
            entityId: editingEvent.id,
            entityLabel: formData.title?.trim(),
            details: 'Zaktualizowano wydarzenie.',
          },
          user
        );
      } else {
        const insertPayload = {
          title: titleToSave || formData.title,
          description: htmlFromBlocks,
          blocks: blocks,
          main_image_path: null,
          start_date: storedDates.start_date,
          end_date: storedDates.end_date,
          location: formData.location,
          status: saveStatus,
          published_at: saveStatus === 'published' ? new Date().toISOString() : null,
          send_push_on_publish: sendPushNotification,
          push_recipient_ids: (isAdmin && selectPushRecipients && selectedPushUserIds.length > 0) ? selectedPushUserIds : null,
        };

        const { data, error } = await supabaseAdmin
          .from('events')
          .insert(insertPayload)
          .select()
          .single();

        if (error) {
          console.error('Supabase insert error:', { code: error.code, message: error.message, payload: insertPayload });
          toast.error(`Błąd podczas zapisywania: ${error.message || 'Nieznany błąd'}`);
          throw error;
        }
        eventId = data.id;
        toast.success('Wydarzenie dodane');
        await logAction(
          {
            section: SECTIONS.EVENTS,
            action: ACTIONS.CREATE,
            entityType: 'event',
            entityId: data.id,
            entityLabel: formData.title?.trim(),
            details: `Dodano wydarzenie: "${formData.title?.trim()}". Status: ${formData.status}.`,
          },
          user
        );
      }

      // Upload gallery images and save to event_images (same pattern as NewsManagement)
      // First image = main image. Order preserved by DELETE + re-insert.
      if (galleryFiles.length > 0 || existingGalleryImages.length > 0) {
        setUploadingGallery(true);
        try {
          // Step 1: Upload all NEW images to home.pl
          const uploadedUrls = [];
          for (let i = 0; i < galleryFiles.length; i++) {
            try {
              const url = await uploadImageToHome(galleryFiles[i], 'events');
              uploadedUrls.push(url);
            } catch (uploadError) {
              console.error(`Error uploading image ${i + 1}:`, uploadError);
              throw new Error(`Błąd przesyłania obrazu ${i + 1}: ${uploadError.message}`);
            }
          }

          // Step 2: DELETE all existing event_images for this event (to preserve order via re-insert)
          if (existingGalleryImages.length > 0) {
            const { error: deleteError } = await supabaseAdmin
              .from('event_images')
              .delete()
              .eq('event_id', eventId);

            if (deleteError) throw new Error(`Błąd usuwania starych obrazów: ${deleteError.message}`);
          }

          // Step 3: Re-insert ALL images in order (existing first, then new). First = main.
          const allImagePaths = [
            ...existingGalleryImages.map(img => img.image_path),
            ...uploadedUrls
          ];

          if (allImagePaths.length > 0) {
            const galleryUploads = allImagePaths.map((imagePath) => ({
              event_id: eventId,
              image_path: imagePath
            }));

            const { error: galleryError } = await supabaseAdmin
              .from('event_images')
              .insert(galleryUploads)
              .select();

            if (galleryError) throw new Error(`Błąd zapisywania galerii: ${galleryError.message || 'Nieznany błąd'}`);

            toast.success(`Galeria zapisana: ${galleryUploads.length} zdjęć`);

            // Step 4: Update events.main_image_path = first image (same as News: first = main)
            const mainImagePath = allImagePaths[0];
            const { error: updateMainError } = await supabaseAdmin
              .from('events')
              .update({ main_image_path: mainImagePath })
              .eq('id', eventId);

            if (updateMainError) {
              console.error('Error updating main_image_path:', updateMainError);
              toast.error('Galeria zapisana, ale nie udało się zaktualizować zdjęcia głównego');
            }
          }
        } catch (galleryUploadError) {
          console.error('Gallery upload error:', galleryUploadError);
          toast.error(galleryUploadError.message || 'Błąd podczas przesyłania galerii');
          throw galleryUploadError;
        } finally {
          setUploadingGallery(false);
        }
      }

      if (saveStatus === 'published' && sendPushNotification && eventId) {
        try {
          const pushTitle = `Nowe wydarzenie: ${titleToSave}`;
          const pushBody = titleToSave.length > 100 ? titleToSave.slice(0, 97) + '...' : titleToSave;
          const body = {
            title: pushTitle,
            body: pushBody,
            type: 'info',
            content_type: 'event',
            data: { type: 'event', screen: 'Events', eventId: String(eventId) },
          };
          if (isAdmin && selectPushRecipients && selectedPushUserIds.length > 0) {
            body.user_ids = selectedPushUserIds;
          }
          const { error: pushError } = await supabaseAdmin.functions.invoke('send-push-notification', { body });
          if (pushError) {
            console.error('Push notification error:', pushError);
            toast.error('Wydarzenie zapisane, ale wysyłka push nie powiodła się');
          } else {
            toast.success('Powiadomienie push wysłane');
          }
        } catch (pushErr) {
          console.error('Push notification error:', pushErr);
          toast.error('Wydarzenie zapisane, ale wysyłka push nie powiodła się');
        }
      }

      resetModalState();
      loadEvents();
    } catch (error) {
      toast.error('Błąd podczas zapisywania');
      console.error(error);
    }
  };

  const handleEdit = async (item) => {
    lastChangeAtRef.current = 0;
    lastAutosaveAtRef.current = 0;
    setEditingEvent(item);
    setFormData({
      title: item.title,
      description: item.description,
      start_date: isoToWarsawDatetimeLocal(item.start_date),
      end_date: isoToWarsawDatetimeLocal(item.end_date),
      location: item.location || '',
      status: item.status,
    });
    const isPublished = item.status === 'published';
    setSendPushNotification(isPublished ? false : (item.send_push_on_publish !== false));
    setSelectPushRecipients(isPublished ? false : (Array.isArray(item.push_recipient_ids) && item.push_recipient_ids.length > 0));
    setSelectedPushUserIds(isPublished ? [] : (Array.isArray(item.push_recipient_ids) ? item.push_recipient_ids : []));
    // Load blocks from DB or convert from description for backward compatibility
    if (item.blocks && Array.isArray(item.blocks) && item.blocks.length > 0) {
      // Ensure all blocks have required properties
      const normalizedBlocks = item.blocks.map(block => {
        if (block.type === 'text') {
          return {
            ...block,
            bold: block.bold ?? false,
            italic: block.italic ?? false,
            underline: block.underline ?? false,
            align: block.align || 'left'
          };
        } else if (block.type === 'link') {
          return {
            ...block,
            text: block.text || '',
            url: block.url || '',
            align: block.align || 'left'
          };
        } else if (block.type === 'facebookVideo') {
          return {
            type: 'facebookVideo',
            url: block.url || '',
          };
        }
        return block;
      });
      setBlocks(normalizedBlocks);
    } else {
      // Convert old description to blocks
      const convertedBlocks = htmlToBlocks(item.description || '');
      setBlocks(convertedBlocks);
    }
    setGalleryFiles([]);
    setGalleryPreviews([]);
    setRemovedImageUrls([]);
    
    // Load event_images separately
    try {
      const { data: imagesData, error: imagesError } = await supabaseAdmin
        .from('event_images')
        .select('*')
        .eq('event_id', item.id);
      
      if (imagesError) {
        console.error('Error loading gallery images:', imagesError);
        setExistingGalleryImages([]);
      } else {
        // Sort by created_at ASC (first image = main image, same as NewsManagement)
        const sorted = (imagesData || []).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        setExistingGalleryImages(sorted);
      }
    } catch (error) {
      console.error('Error loading gallery images:', error);
      setExistingGalleryImages([]);
    }

    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to wydarzenie?')) return;

    try {
      const { data: eventRow, error: eventFetchError } = await supabaseAdmin
        .from('events')
        .select('main_image_path')
        .eq('id', id)
        .single();
      if (eventFetchError && eventFetchError.code !== 'PGRST116') throw eventFetchError;

      const { data: eventImages, error: imagesFetchError } = await supabaseAdmin
        .from('event_images')
        .select('image_path')
        .eq('event_id', id);
      if (imagesFetchError) throw imagesFetchError;

      let urls = [];
      if (eventRow?.main_image_path && (eventRow.main_image_path.startsWith('http://') || eventRow.main_image_path.startsWith('https://'))) {
        urls.push(eventRow.main_image_path);
      }
      (eventImages || []).forEach((row) => {
        if (row.image_path && (row.image_path.startsWith('http://') || row.image_path.startsWith('https://'))) {
          urls.push(row.image_path);
        }
      });
      if (urls.length > 0) {
        const { data: otherEventsRows } = await supabaseAdmin.from('events').select('main_image_path').neq('id', id);
        const { data: otherEventImages } = await supabaseAdmin.from('event_images').select('image_path').neq('event_id', id);
        const usedElsewhere = new Set([
          ...(otherEventsRows || []).map((r) => r.main_image_path).filter(Boolean),
          ...(otherEventImages || []).map((r) => r.image_path).filter(Boolean),
        ]);
        urls = urls.filter((path) => !usedElsewhere.has(path));
        if (urls.length > 0) await deleteImagesOnHome(urls);
      }

      // 1) Delete related event_images first (same order as news: news_images then news)
      const { error: eventImagesError } = await supabaseAdmin
        .from('event_images')
        .delete()
        .eq('event_id', id);
      if (eventImagesError) {
        console.error('event_images delete error:', eventImagesError);
        throw new Error(eventImagesError.message || 'Błąd podczas usuwania zdjęć wydarzenia');
      }

      // 2) Then delete the event record
      const { error } = await supabaseAdmin
        .from('events')
        .delete()
        .eq('id', id);
      if (error) {
        console.error('Supabase delete error:', { code: error.code, message: error.message });
        toast.error(`Błąd podczas usuwania: ${error.message || 'Nieznany błąd'}`);
        throw error;
      }
      toast.success('Wydarzenie usunięte');
      const item = events.find((e) => e.id === id);
      await logAction(
        {
          section: SECTIONS.EVENTS,
          action: ACTIONS.DELETE,
          entityType: 'event',
          entityId: id,
          entityLabel: item?.title,
          details: item?.status === 'draft' ? 'Usunięto wydarzenie (draft).' : 'Usunięto wydarzenie.',
        },
        user
      );
      loadEvents();
    } catch (error) {
      toast.error(error.message || 'Błąd podczas usuwania');
      console.error('Error deleting event:', error);
    }
  };

  const handlePublish = async (id, currentStatus, itemFromList) => {
    try {
      const item = itemFromList || events.find((e) => e.id === id);
      if (currentStatus === 'draft' && item?.parent_id) {
        const { data: draftRow, error: fetchErr } = await supabaseAdmin
          .from('events')
          .select('*')
          .eq('id', id)
          .single();
        if (fetchErr || !draftRow) throw new Error(fetchErr?.message || 'Nie znaleziono szkicu');
        const parentId = draftRow.parent_id;
        const { error: updateParentError } = await supabaseAdmin
          .from('events')
          .update({
            title: draftRow.title,
            description: draftRow.description,
            blocks: draftRow.blocks,
            start_date: draftRow.start_date,
            end_date: draftRow.end_date,
            location: draftRow.location,
            status: 'published',
            updated_at: new Date().toISOString(),
            send_push_on_publish: draftRow.send_push_on_publish,
            push_recipient_ids: draftRow.push_recipient_ids,
          })
          .eq('id', parentId);
        if (updateParentError) throw updateParentError;
        const { data: draftImages } = await supabaseAdmin.from('event_images').select('image_path').eq('event_id', id);
        await supabaseAdmin.from('event_images').delete().eq('event_id', parentId);
        if (draftImages?.length) {
          await supabaseAdmin.from('event_images').insert(
            draftImages.map((row) => ({ event_id: parentId, image_path: row.image_path }))
          );
          await supabaseAdmin.from('events').update({ main_image_path: draftImages[0].image_path }).eq('id', parentId);
        }
        await supabaseAdmin.from('event_images').delete().eq('event_id', id);
        await supabaseAdmin.from('events').delete().eq('id', id);
        toast.success('Opublikowano (wersja robocza nadpisuje opublikowane)');
        await logAction(
          { section: SECTIONS.EVENTS, action: ACTIONS.STATUS_CHANGE, entityType: 'event', entityId: parentId, entityLabel: item?.title, details: 'Opublikowano wersję roboczą nadpisując opublikowane wydarzenie.', extra: { oldStatus: 'draft', newStatus: 'published' } },
          user
        );
        const shouldSendPush = draftRow.send_push_on_publish !== false;
        if (shouldSendPush) {
          try {
            const pushTitle = `Nowe wydarzenie: ${draftRow.title || ''}`;
            const pushBody = (draftRow.title || '').length > 100 ? (draftRow.title || '').slice(0, 97) + '...' : (draftRow.title || '');
            const body = {
              title: pushTitle,
              body: pushBody,
              type: 'info',
              content_type: 'event',
              data: { type: 'event', screen: 'Events', eventId: String(parentId) },
            };
            if (isAdmin && Array.isArray(draftRow.push_recipient_ids) && draftRow.push_recipient_ids.length > 0) {
              body.user_ids = draftRow.push_recipient_ids;
            }
            const { error: pushError } = await supabaseAdmin.functions.invoke('send-push-notification', { body });
            if (pushError) {
              console.error('Push notification error:', pushError);
              toast.error('Wydarzenie zapisane, ale wysyłka push nie powiodła się');
            } else {
              toast.success('Powiadomienie push wysłane');
            }
          } catch (pushErr) {
            console.error('Push notification error:', pushErr);
            toast.error('Wydarzenie zapisane, ale wysyłka push nie powiodła się');
          }
        }
        loadEvents();
        return;
      }
      const newStatus = currentStatus === 'published' ? 'draft' : 'published';
      const publishedAt = newStatus === 'published' ? (item?.published_at || new Date().toISOString()) : null;
      const { error } = await supabaseAdmin
        .from('events')
        .update({
          status: newStatus,
          published_at: publishedAt,
        })
        .eq('id', id);

      if (error) {
        console.error('Supabase publish error:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        toast.error(`Błąd podczas zmiany statusu: ${error.message || 'Nieznany błąd'}`);
        throw error;
      }
      toast.success(newStatus === 'published' ? 'Opublikowano' : 'Cofnięto publikację');
      await logAction(
        {
          section: SECTIONS.EVENTS,
          action: ACTIONS.STATUS_CHANGE,
          entityType: 'event',
          entityId: id,
          entityLabel: item?.title,
          details: `Status: ${currentStatus} → ${newStatus}.`,
          extra: { oldStatus: currentStatus, newStatus },
        },
        user
      );
      loadEvents();
    } catch (error) {
      console.error('Error publishing event:', error);
    }
  };

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  return (
    <div className="news-management">
      <div className="page-header">
        <h2>Zarządzanie wydarzeniami</h2>
        <button className="btn btn-primary" onClick={() => {
          lastChangeAtRef.current = 0;
          lastAutosaveAtRef.current = 0;
          setShowModal(true);
        }}>
          Dodaj wydarzenie
        </button>
      </div>

      <div className="tabs-row" style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
        <button
          type="button"
          className={`btn ${listTab === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setListTab('all')}
        >
          Opublikowane
        </button>
        <button
          type="button"
          className={`btn ${listTab === 'drafts' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setListTab('drafts')}
        >
          Robocze
        </button>
      </div>
      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Tytuł</th>
              <th>Data rozpoczęcia</th>
              <th>Status</th>
              <th>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {displayedEvents.length === 0 ? (
              <tr>
                <td colSpan="4" className="empty-state">
                  {listTab === 'drafts' ? 'Brak wersji roboczych' : 'Brak opublikowanych wydarzeń'}
                </td>
              </tr>
            ) : (
              displayedEvents.map((item) => (
                <tr key={item.id}>
                  <td>{item.title}</td>
                  <td>{formatWarsawDateTime(item.start_date)}</td>
                  <td>
                    <span className={`badge ${item.status === 'published' ? 'badge-success' : 'badge-warning'}`}>
                      {item.status === 'published' ? 'Opublikowane' : 'Szkic'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                        {listTab !== 'drafts' && (
                          <button
                            className="btn btn-secondary"
                            onClick={() => handlePublish(item.id, item.status, item)}
                          >
                            Cofnij
                          </button>
                        )}
                      <button
                        className="btn btn-primary"
                        onClick={() => handleEdit(item)}
                      >
                        Edytuj
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDelete(item.id)}
                      >
                        Usuń
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay wide-modal-overlay" onClick={() => { if (!uploadingGallery) handleCloseModal(); }}>
          <div className="modal-content wide-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingEvent ? 'Edytuj wydarzenie' : 'Dodaj wydarzenie'}
              </h3>
              <button
                className="modal-close"
                onClick={() => { if (!uploadingGallery) handleCloseModal(); }}
                disabled={uploadingGallery}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Tytuł *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.title}
                  onChange={(e) => {
                    lastChangeAtRef.current = Date.now();
                    setFormData({ ...formData, title: e.target.value });
                  }}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Opis *</label>
                <BlockEditor
                  blocks={blocks}
                  onBlocksChange={(newBlocks) => {
                    lastChangeAtRef.current = Date.now();
                    setBlocks(newBlocks);
                  }}
                  onImageUpload={uploadImage}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Data rozpoczęcia *</label>
                <input
                  type="datetime-local"
                  className="form-input"
                  value={formData.start_date}
                  onChange={(e) => {
                    lastChangeAtRef.current = Date.now();
                    setFormData({ ...formData, start_date: e.target.value });
                  }}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Data zakończenia</label>
                <input
                  type="datetime-local"
                  className="form-input"
                  value={formData.end_date}
                  onChange={(e) => {
                    lastChangeAtRef.current = Date.now();
                    setFormData({ ...formData, end_date: e.target.value });
                  }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Lokalizacja</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.location}
                  onChange={(e) => {
                    lastChangeAtRef.current = Date.now();
                    setFormData({ ...formData, location: e.target.value });
                  }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Galeria zdjęć wydarzenia (pierwsze zdjęcie = zdjęcie główne)</label>
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={handleGallerySelect}
                  className="form-input"
                  disabled={uploadingGallery}
                />
                {uploadingGallery && (
                  <div className="upload-loader">Przesyłanie galerii...</div>
                )}
                {(galleryPreviews.length > 0 || existingGalleryImages.length > 0) && (
                  <div className="gallery-preview-container">
                    {/* Nowe zdjęcia (przed zapisem) - same as NewsManagement */}
                    {galleryPreviews.map((preview, index) => (
                      <div key={`new-${index}`} className="gallery-item">
                        <img src={preview.preview} alt={`Preview ${index + 1}`} className="gallery-preview" />
                        {index === 0 && existingGalleryImages.length === 0 && (
                          <div className="main-image-badge">Główne</div>
                        )}
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => removeGalleryFile(index)}
                          disabled={uploadingGallery}
                        >
                          Usuń
                        </button>
                        <div className="gallery-controls">
                          <button
                            type="button"
                            className="move-btn"
                            onClick={() => moveGalleryImageUp(index, false)}
                            disabled={uploadingGallery || index === 0}
                            title="Przenieś w górę"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="move-btn"
                            onClick={() => moveGalleryImageDown(index, false)}
                            disabled={uploadingGallery || index === galleryPreviews.length - 1}
                            title="Przenieś w dół"
                          >
                            ↓
                          </button>
                        </div>
                      </div>
                    ))}
                    {/* Istniejące zdjęcia (z bazy) - same as NewsManagement */}
                    {existingGalleryImages.map((img, index) => (
                      <div key={img.id} className="gallery-item">
                        <img src={getPublicImageUrl(img.image_path)} alt="Gallery" className="gallery-preview" />
                        {index === 0 && <div className="main-image-badge">Główne</div>}
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => markGalleryImageForRemoval(img.image_path)}
                          disabled={uploadingGallery}
                        >
                          Usuń
                        </button>
                        <div className="gallery-controls">
                          <button
                            type="button"
                            className="move-btn"
                            onClick={() => moveGalleryImageUp(index, true)}
                            disabled={uploadingGallery || index === 0}
                            title="Przenieś w górę"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="move-btn"
                            onClick={() => moveGalleryImageDown(index, true)}
                            disabled={uploadingGallery || index === existingGalleryImages.length - 1}
                            title="Przenieś w dół"
                          >
                            ↓
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-input"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  disabled={uploadingGallery}
                >
                  <option value="draft">Szkic</option>
                  <option value="published">Opublikowane</option>
                </select>
              </div>
              <div className="form-group" style={editingEvent?.status === 'published' ? { opacity: 0.65, pointerEvents: 'none' } : undefined}>
                <label className="form-label">Powiadomienie push</label>
                <label className="checkbox-label" style={{ display: 'block', marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    checked={editingEvent?.status === 'published' ? false : sendPushNotification}
                    onChange={(e) => setSendPushNotification(e.target.checked)}
                    disabled={uploadingGallery || editingEvent?.status === 'published'}
                  />
                  <span>Wyślij push powiadomienie o nowym wydarzeniu (po opublikowaniu)</span>
                </label>
                {isAdmin && (
                  <>
                    <label className="checkbox-label" style={{ display: 'block', marginBottom: 8 }}>
                      <input
                        type="checkbox"
                        checked={editingEvent?.status === 'published' ? false : selectPushRecipients}
                        onChange={(e) => {
                          setSelectPushRecipients(e.target.checked);
                          if (!e.target.checked) setSelectedPushUserIds([]);
                        }}
                        disabled={uploadingGallery || editingEvent?.status === 'published'}
                      />
                      <span>Wybierz odbiorców (domyślnie: wszyscy użytkownicy)</span>
                    </label>
                    {selectPushRecipients && !(editingEvent?.status === 'published') && (
                      <div className="push-recipients-list" style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto', border: '1px solid #ddd', padding: 8, borderRadius: 4 }}>
                        <p className="form-hint" style={{ marginBottom: 8 }}>Zaznacz użytkowników, do których wysłać push:</p>
                        {pushRecipientsList.length === 0 ? (
                          <p className="info-text">Ładowanie listy...</p>
                        ) : (
                          <div className="push-recipients-checkboxes">
                            {pushRecipientsList.map((u) => (
                              <label key={u.id} className="checkbox-label push-recipient-item" style={{ display: 'block', marginBottom: 4 }}>
                                <input
                                  type="checkbox"
                                  checked={selectedPushUserIds.includes(u.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedPushUserIds((prev) => [...prev, u.id]);
                                    } else {
                                      setSelectedPushUserIds((prev) => prev.filter((id) => id !== u.id));
                                    }
                                  }}
                                />
                                <span>{u.device_id || `ID: ${u.id}`}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        {selectedPushUserIds.length > 0 && (
                          <p className="form-hint" style={{ marginTop: 8 }}>Wybrano: {selectedPushUserIds.length} użytkowników</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={uploadingGallery}
                >
                  {uploadingGallery ? 'Zapisywanie...' : (editingEvent ? 'Zaktualizuj' : 'Dodaj')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { if (!uploadingGallery) handleCloseModal(); }}
                  disabled={uploadingGallery}
                >
                  Anuluj
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

